"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useState } from "react"

type Facets = Record<string, Record<string, number>>
const SEGMENT_LABELS: Record<string, string> = { "Health Practioners": "Health Practitioners" }

/** Filter panel: brand, category, retailer type, GST-free. Multi-select within a group (OR), AND across groups. */
export default function FacetPanel({ facets, selected, total }: { facets: Facets; selected: { brand: string[]; cat: string[]; seg: string[]; gstFree: boolean }; total: number }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [openMobile, setOpenMobile] = useState(false)

  const push = (params: URLSearchParams) => {
    params.delete("page")
    router.push(`${pathname}${params.toString() ? `?${params.toString()}` : ""}`, { scroll: false })
  }
  const toggle = (key: "brand" | "cat" | "seg", value: string) => {
    const p = new URLSearchParams(searchParams)
    const cur = (p.get(key) ?? "").split("|").filter(Boolean)
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
    next.length ? p.set(key, next.join("|")) : p.delete(key)
    if (key === "seg") p.delete("tag")
    push(p)
  }
  const setGst = (on: boolean) => { const p = new URLSearchParams(searchParams); on ? p.set("gstfree", "1") : p.delete("gstfree"); push(p) }
  const clearAll = () => { const p = new URLSearchParams(searchParams); ["brand", "cat", "seg", "gstfree", "tag"].forEach((k) => p.delete(k)); push(p) }
  const activeCount = selected.brand.length + selected.cat.length + selected.seg.length + (selected.gstFree ? 1 : 0)

  const group = (title: string, key: "brand" | "cat" | "seg", dist: Record<string, number> = {}, chosen: string[]) => {
    const entries = Object.entries(dist).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    for (const c of chosen) if (!dist[c]) entries.push([c, 0])
    if (!entries.length) return null
    return (
      <fieldset className="flex flex-col gap-1 py-3 border-t border-elevate-border first:border-t-0">
        <legend className="text-xs uppercase tracking-[0.12em] text-elevate-grey-mid mb-1">{title}</legend>
        {entries.map(([value, count]) => (
          <label key={value} className="flex items-center justify-between gap-2 text-sm min-h-8 cursor-pointer">
            <span className="flex items-center gap-2">
              <input type="checkbox" checked={chosen.includes(value)} onChange={() => toggle(key, value)} className="h-4 w-4 accent-elevate-black" />
              {SEGMENT_LABELS[value] ?? value}
            </span>
            <span className="text-xs text-elevate-grey-mid">{count}</span>
          </label>
        ))}
      </fieldset>
    )
  }

  const body = (
    <div className="flex flex-col">
      <div className="flex items-center justify-between pb-2">
        <span className="text-sm font-medium">{total} products</span>
        {activeCount > 0 && <button type="button" onClick={clearAll} className="text-xs underline underline-offset-4">Clear filters ({activeCount})</button>}
      </div>
      <label className="flex items-center gap-2 text-sm min-h-8 py-2 border-t border-elevate-border cursor-pointer">
        <input type="checkbox" checked={selected.gstFree} onChange={(e) => setGst(e.target.checked)} className="h-4 w-4 accent-elevate-black" />
        GST-free only <span className="text-xs text-elevate-grey-mid">({facets.gst_free?.["true"] ?? 0})</span>
      </label>
      {group("Category", "cat", facets.categories, selected.cat)}
      {group("Brand", "brand", facets.brand, selected.brand)}
      {group("Popular with", "seg", facets.segments, selected.seg)}
    </div>
  )

  return (
    <>
      <div className="hidden small:block rounded-lg bg-white shadow-borders-base px-4 py-3">{body}</div>
      <div className="small:hidden">
        <button type="button" onClick={() => setOpenMobile((o) => !o)} aria-expanded={openMobile} className="w-full flex items-center justify-between rounded-lg bg-white shadow-borders-base px-4 py-3 text-sm font-medium">
          Filters{activeCount ? ` (${activeCount})` : ""} <span aria-hidden>{openMobile ? "−" : "+"}</span>
        </button>
        {openMobile && <div className="mt-2 rounded-lg bg-white shadow-borders-base px-4 py-3">{body}</div>}
      </div>
    </>
  )
}
